import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import Sheet from '@mui/joy/Sheet';
import Button from '@mui/joy/Button';
import AssetActions from './AssetActions';
import IconButton from "@mui/joy/IconButton";
import Skeleton from 'react-loading-skeleton';
import CssBaseline from '@mui/joy/CssBaseline';
import LoadingIcon from '../../../common/LoadingIcon';
import ToastMessage from '../../../common/ToastMessage';
import useScreenWidth from '../../../store/useScreenWidth';
import CreateRoundedIcon from '@mui/icons-material/CreateRounded';
import useAddAsset from '../../../store/ReactQueries/assets/asset/useAddAsset';
import useAssetById from '../../../store/ReactQueries/assets/asset/useAssetById';
import useUpdateAsset from '../../../store/ReactQueries/assets/asset/useUpdateAsset';
import { EndPoints } from '../../../utils/apiEndpoints';
import { useNavigate, useParams } from 'react-router-dom';
import { FieldErrors } from '../../../domain/helpers/Errors';
import { validateSchema } from '../../../common/ValidateSchema';
import { AssetInterface } from '../../../models/AssetModel/Asset';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { useDarkModeStore } from '../../../store/useDarkModeStore';
import { assetSchema } from '../../../domain/ValidationSchemas/Asset/asset';
import { darkModeBackgroudInputs, PAGESIZE } from '../../../utils/constants';
import CustomizedInputsStyled, { AssetTypeInputs } from './CustomizedInputStyled';

interface AddAssetProps {
    view?: boolean;
}

export interface GenerateInput<T> {
    data?: T;
    id: number;
    name: string;
    label: string;
    type: AssetTypeInputs;
}

export const AddAsset: React.FC<AddAssetProps> = ({ view = false }) => {
    const navigate = useNavigate();
    const isSmallScreen = useScreenWidth();

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const { id: assetId } = useParams<{ id: string }>();
    const { data: assetById, error: assetError, isLoading } = useAssetById(assetId);

    const page = 1;
    const pageSize = PAGESIZE;

    const addNewAsset = useAddAsset(page, pageSize);
    const updateAsset = useUpdateAsset(page, pageSize);
    const [loading, setLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [disableSaveBtn, setDisableBtnSave] = useState<boolean>(false);

    const initialState: AssetInterface = {
        id: '',
        barcode: '',
        assetCategoryId: '',
        title: '',
        assetTypeId: '',
        description: '',
        assetDepartmentId: '',
        assetStatusId: '',
        price: 0,
        companyId: '',
        customerId: '',
        locationId: '',
    };

    const [form, setForm] = useState<AssetInterface>(initialState);

    useEffect(() => {
        if (assetError) {
            ToastMessage.Failed(`Error: ${assetError.message}`);
            navigate(-1);
        }
    }, [assetError]);

    useEffect(() => {
        if (assetId && assetById) {
            let result = assetById as AssetInterface;
            setForm(result);
        }
    }, [assetId, assetById]);

    useEffect(() => {
        setLoading(true);

        const timer = setTimeout(() => {
            setLoading(false);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    const generateInputs: GenerateInput<any>[] = [
        { id: 1, name: 'title', label: 'Title', type: 'text' },
        { id: 2, name: 'barcode', label: 'Barcode', type: 'text' },
        { id: 3, name: 'companyId', label: 'Company', type: 'select' },
        { id: 4, name: 'assetCategoryId', label: 'AssetCategories', type: 'select' },
        { id: 5, name: 'assetTypeId', label: 'AssetTypes', type: 'select' },
        { id: 6, name: 'description', label: 'Description', type: 'multitext' },
        { id: 7, name: 'locationId', label: 'Location', type: 'select' },
        { id: 8, name: 'customerId', label: 'Customer', type: 'select' },
        { id: 9, name: 'assetDepartmentId', label: 'AssetDepartments', type: 'select' },
        { id: 10, name: 'assetStatusId', label: 'AssetStatuses', type: 'select' },
        { id: 11, name: 'price', label: 'Price', type: 'number' },
    ];

    const handleSuccess = (resolve: (value: void | PromiseLike<void>) => void) => {
        ToastMessage.Success();
        navigate(EndPoints.ASSETS);
        resolve();
    };

    const handleSave = async () => {
        const isValid = validateSchema(assetSchema,
            {
                ...form,
                customerId: form.customerId ? form.customerId : '',
                locationId: form.locationId ? form.locationId : '',
            });
        setErrors(isValid.errors);
        if (!isValid.result) return;

        setDisableBtnSave(true);

        try {
            await new Promise<void>((resolve, reject) => {
                const { id, barcode, assetCategoryId, title, assetTypeId, description, locationId, assetDepartmentId, assetStatusId, price, companyId, customerId } = form;
                let upForm: AssetInterface = {
                    id, barcode, assetCategoryId, title, assetTypeId, description, assetDepartmentId, assetStatusId, price, companyId,
                    customerId: customerId ? customerId : undefined,
                    locationId: locationId ? locationId : undefined,
                }
                if (upForm.id === '') {
                    const { id, ...formData } = upForm;
                    addNewAsset.mutate(formData, {
                        onSuccess: () => handleSuccess(resolve),
                        onError: (error) => ToastMessage.Failed(error.message),
                    });
                } else {
                    updateAsset.mutate(upForm, {
                        onSuccess: () => handleSuccess(resolve),
                        onError: (error) => ToastMessage.Failed(error.message),
                    });
                }
            });
        } catch (ex: unknown) {
            if (ex instanceof Error) {
                console.log(ex.message);
            } else {
                console.log("An unknown error occurred");
            }
        }
        setTimeout(() => {
            setDisableBtnSave(false);
        }, 300);
    };

    const cssSheet = {
        my: 1,
        py: 4,
        px: 4,
        gap: 0,
        display: 'flex',
        borderRadius: 'md',
        flexDirection: 'column',
        width: isSmallScreen ? '90%' : '60%',
        boxShadow: '8px 8px 30px rgba(0, 0, 0, 0.2)',
        background: darkMode ? 'var(--dark-mode-bg)' : 'white'
    };

    return (
        <div className='w-100 d-flex justify-content-center '>
            <CssBaseline />
            {isLoading
                ? <LoadingIcon width={50} />
                : <div className='d-flex flex-column align-items-center gap-2 w-100'>
                    <Sheet sx={cssSheet}>
                        <h5 className='mb-0 ' style={{ color: darkMode ? 'white' : 'rgba(0, 0, 0, 0.6)' }}>
                            {language['Asset']}
                            <span className='ms-1 me-1'>-</span>
                            {
                                view
                                    ? language['View']
                                    : assetId === undefined
                                        ? language['Add']
                                        : loading
                                            ? <Skeleton style={{ width: 100, height: 22 }} />
                                            : assetById && assetById.barcode
                            }
                            {
                                view
                                && <IconButton
                                    className={`ms-2 ${darkMode && 'text-white'}`}
                                    title={language['EditAsset']}
                                    onClick={() => navigate(`${EndPoints.ASSETS}/edit/${form.id}`)}
                                >
                                    <CreateRoundedIcon sx={{ width: 18, height: 18 }} />
                                </IconButton>
                            }
                        </h5>
                        <hr className={darkMode ? 'text-white opacity-100' : ''} />
                        <form
                            className={`${isSmallScreen ? 'd-flex flex-column' : 'module-form-grid'} gap-2`}
                            style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}
                        >
                            {
                                generateInputs?.map(({ id, name, label, type }) =>
                                    <CustomizedInputsStyled
                                        key={id}
                                        name={name}
                                        type={type}
                                        form={form}
                                        view={view}
                                        label={label}
                                        errors={errors}
                                        loading={loading}
                                        setForm={setForm}
                                    />
                                )
                            }
                        </form>
                        <hr className={darkMode ? 'text-white opacity-100' : ''} />
                        {
                            view
                            && <React.Fragment>
                                <AssetActions
                                    assetById={assetById}
                                />
                                <hr className={darkMode ? 'text-white opacity-100' : ''} />
                            </React.Fragment>
                        }
                        <div className='d-flex flex-row justify-content-end gap-2'>
                            <Button
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    background: darkMode ? darkModeBackgroudInputs : "white",
                                    border: `1px solid var(--btn-color)`,
                                    color: darkMode ? 'white' : "var(--btn-color)",
                                    "&:hover": {
                                        background: darkMode ? darkModeBackgroudInputs : "white",
                                        opacity: .7,
                                    },
                                }}
                                onClick={() => navigate(EndPoints.ASSETS)}
                            >
                                {language['Cancel']}
                            </Button>
                            {
                                !view
                                && <Button
                                    sx={{
                                        width: 85,
                                        color: "white",
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        background: "var(--btn-color)",
                                        "&:hover": {
                                            opacity: .7,
                                            background: "var(--btn-color)",
                                        },
                                    }}
                                    onClick={handleSave}
                                >
                                    {
                                        disableSaveBtn
                                            ? <LoadingIcon width={22} />
                                            : language['Save']
                                    }
                                </Button>
                            }
                        </div>
                        {
                            assetById
                            && <div
                                className={`position-absolute d-flex flex-row gap-1 ${darkMode ? 'text-white' : 'text-muted'}`}
                                style={{ bottom: 10, left: 10, fontSize: 11 }}
                            >
                                <span>{language['CreatedAt']}: {assetById.createdDate ? dayjs(assetById.createdDate).format('DD/MM/YYYY HH:mm') : ''}</span>

                                {assetById.author && <span>{language['by']} {`${assetById.author.firstname ?? ''} ${assetById.author.lastname ?? ''}`}</span>}
                            </div>
                        }
                    </Sheet>
                </div>
            }
        </div>
    );
}
export default AddAsset